import ZTable, { IColumnType, ITableRef } from '@/components/ZTable';
import { ModalWidth, formatDisplayFullDate } from '@/config';
import { PrinterType, TicketStatusType } from '@/constants';
import { getFullName } from '@/helpers/string';
import { Tooltip, Button, Modal } from 'antd';
import { t } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PrinterFilled } from '@ant-design/icons';
import bixolonWebPrintService from '@/services/bixolonWebPrintService';
import { BookingPagingDto, TicketPagingDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<TicketPagingDto>[] = [
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
    sorter: true,
    width: 180,
  },
  {
    title: 'ticketBooking_TicketRefNo',
    dataIndex: 'ticketRefNo',
    sorter: true,
    ellipsis: true,
    width: 200,
  },
  {
    title: 'base.Status',
    dataIndex: 'ticketStatus',
    sorter: true,
    ellipsis: true,
    render: v => TicketStatusType.__getValue(v),
    width: 130,
  },
  {
    title: 'ticketBooking_Passenger',
    dataIndex: 'passengerFirstName',
    ellipsis: true,
    width: 180,
    render: (_, record) => getFullName(record.passengerFirstName, record.passengerLastName),
  },
  {
    title: 'ticketBooking_SeatNo',
    dataIndex: 'seatName',
    ellipsis: true,
    width: 80,
  },
  {
    title: 'ticketBooking_TicketClassName',
    dataIndex: 'ticketClassName',
    ellipsis: true,
    width: 130,
  },
  {
    title: 'ticketBooking_BookingDate',
    dataIndex: 'createdOn',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
];

type Props = {
  booking: BookingPagingDto;

  onClose: () => void;
};

export default function TicketsModal({ booking, onClose }: Props) {
  const zTb = React.useRef<ITableRef>();
  const { t } = useTranslation();
  const printTicket = (ticketRefNo: string) => {
    apiSdk.StationPrinterService.getStationPrintersByRegister({ registerId: booking.registerId || '' }).then(printers => {
      apiSdk.BookingDetailService.getBookingDetailsForPrinting({ id: booking.bookingId || '' }).then(res => {
        const printer = bixolonWebPrintService.getAvailablePrinter(PrinterType.Tags, printers);
        bixolonWebPrintService.printLabelTicket(printer, res, ticketRefNo);
      });
    });
  };

  return (
    <Modal
      width={ModalWidth.extraLarge}
      title={t('bookingPage_Tickets')}
      onOk={onClose}
      okText={t('Close')}
      cancelButtonProps={{ style: { display: 'none' } }}
      open={true}
      onCancel={onClose}
    >
      {booking.bookingId ? (
        <ZTable<TicketPagingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/bookings/booking-details/get-ticket-paging"
          defaultFilter={{ bookingId: booking.bookingId }}
          bordered
          order_by_asc={true}
          allowEdit={false}
          allowDelete={false}
          searchBtnAlignLeft
          actionColumnWidth={80}
          extraActions={[
            {
              render: record => {
                return (
                  <Tooltip title={t('bookingPage_PrintTicket')}>
                    <Button
                      size="small"
                      onClick={() => {
                        printTicket(record.ticketRefNo || '');
                      }}
                      type="link"
                      icon={<PrinterFilled />}
                    />
                  </Tooltip>
                );
              },
            },
          ]}
        />
      ) : null}
    </Modal>
  );
}
